import { useState } from "react";
import type { PaymentMethod } from "@app-types";
import { PaymentMethodBadge } from "../payment-method-badge";
import { TrashIcon } from "@icons";
import { useAllowedPaymentTypes } from "@hooks";
import Modal from "./modal";
import ConfirmModalContent from "./confirm-modal-content";
import { Tooltip } from "./tooltip";
import { Badge } from "./badge";
import { Button, IconButton } from "./button";

export const PaymentMethodCard = ({
    paymentMethod,
    isDefault,
    isDefaultOnOtherAccount,
    onDelete,
    makeDefault,
    onChange,
    idx
}: {
    paymentMethod: Omit<PaymentMethod, `accountPaymentMethods`>;
    isDefault: boolean;
    isDefaultOnOtherAccount?: boolean;
    onDelete?: (idPaymentMethod: number) => void;
    makeDefault?: (idPaymentMethod: number) => void;
    onChange?: (idPaymentMethod: number) => void;
    idx?: number;
}) => {
    const { idPaymentMethod, sourceName, accountType, last4, idPaymentType } = paymentMethod;
    const { allowedPaymentTypes, isLoading: allowedPaymentTypesIsLoading } = useAllowedPaymentTypes();
    const [showMakeDefaultModal, setShowMakeDefaultModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCurrentDefaultBlockModal, setShowCurrentDefaultBlockModal] = useState(false);
    const [showOtherAccountDefaultBlockModal, setShowOtherAccountDefaultBlockModal] = useState(false);

    // Determine if payment method is disabled.
    //
    // We filter disabled methods out of view on the Payment Settings page.  The
    // only other places we render PaymentMethodCard are when setting up autopay
    // during onboarding, and when resolving default payment methods among
    // multiple residents; in both cases, we wouldn't expect a payment method to
    // be disabled.  Therefore, the disabled state is unlikely to ever occur in
    // the app.  However, we maintain it because - in theory - we would want to
    // provide some visual indication of it, if it ever occurred.
    const isDisabled =
        !!paymentMethod.disabledAt ||
        (!allowedPaymentTypes.includes(Number(idPaymentType ?? -1)) && !allowedPaymentTypesIsLoading);

    // Determine if this is an ACH payment method
    const isACH = accountType === `Checking` || accountType === `Savings`;

    // Get display name for payment method
    const getPaymentMethodDisplayName = () => {
        if (isACH) {
            return accountType || `ACH`;
        }
        return sourceName || `Card`;
    };

    return (
        <div
            key={idPaymentMethod}
            id={`pm-${idx}`}
            className={`${isDisabled ? `bg-neutral-50` : `bg-white`} border border-neutral-100 flex items-center justify-between pl-6 pr-4 py-4 rounded-xl`}
        >
            {/* Left side: Payment method info */}
            <div className="flex items-center gap-2 md:gap-4">
                {/* Payment method name and badge/last4 */}
                <div className="flex items-center gap-2 md:gap-4">
                    <span className="text-base leading-6 font-normal text-neutral-950 tracking-[0.16px]">
                        {getPaymentMethodDisplayName()}
                    </span>

                    {/* Badge and last 4 digits */}
                    <div className="flex items-center gap-1">
                        <PaymentMethodBadge
                            paymentType={Number(idPaymentType)}
                            sourceName={sourceName}
                        />
                        <span className="text-sm leading-5 font-normal text-neutral-700 tracking-[0.14px]">
                            {last4}
                        </span>
                    </div>
                </div>

                {/* Badge - Show only one at a time, Disabled takes priority */}
                {isDisabled ? (
                    <Badge color="red">Disabled</Badge>
                ) : isDefault ? (
                    <Badge color="blue">Default</Badge>
                ) : null}
            </div>

            {/* Right side: Action buttons */}
            <div className="flex items-center gap-2">
                {/* Change button */}
                {onChange && (
                    <Button
                        variant="tertiary"
                        size="xs"
                        onClick={() => onChange(idPaymentMethod)}
                        aria-label="Change payment method"
                    >
                        Change
                    </Button>
                )}

                {/* Make default button */}
                {makeDefault && !isDefault && (
                    <>
                        <Button
                            variant="tertiary"
                            size="sm"
                            onClick={() => setShowMakeDefaultModal(true)}
                            aria-label="Make default payment method"
                        >
                            <span className="hidden md:inline">Make Default</span>
                            <span className="md:hidden">Default</span>
                        </Button>
                        <Modal
                            isOpen={showMakeDefaultModal}
                            onClose={() => setShowMakeDefaultModal(false)}
                            ariaLabelledby="modal-title"
                        >
                            <ConfirmModalContent
                                title="Make Default Payment Method"
                                message="Marking this payment method as the default on your account will assign it to all payments moving forward, including Autopay."
                                actionText="Okay"
                                onAction={() => {
                                    makeDefault(idPaymentMethod);
                                    setShowMakeDefaultModal(false);
                                }}
                                showCancel={true}
                                onCancel={() => setShowMakeDefaultModal(false)}
                            />
                        </Modal>
                    </>
                )}

                {/* Delete button */}
                {onDelete && (
                    <>
                        <Tooltip
                            content="Delete payment method"
                            position="top"
                        >
                            <IconButton
                                variant="tertiary"
                                size="lg"
                                dangerOnHover
                                onClick={() => {
                                    if (isDefault) {
                                        setShowCurrentDefaultBlockModal(true);
                                    } else if (isDefaultOnOtherAccount) {
                                        setShowOtherAccountDefaultBlockModal(true);
                                    } else {
                                        setShowDeleteModal(true);
                                    }
                                }}
                                aria-label="Delete payment method"
                            >
                                <TrashIcon />
                            </IconButton>
                        </Tooltip>

                        {/* Blocked: default on current account */}
                        <Modal
                            isOpen={showCurrentDefaultBlockModal}
                            onClose={() => setShowCurrentDefaultBlockModal(false)}
                            ariaLabelledby="modal-title"
                        >
                            <ConfirmModalContent
                                title="Payment Method Set As Default"
                                message={<>To delete this payment method, first assign another payment method as your <Badge color="blue">Default</Badge> payment method.</>}
                                actionText="Okay"
                                showCancel={false}
                                onAction={() => setShowCurrentDefaultBlockModal(false)}
                                onCancel={() => setShowCurrentDefaultBlockModal(false)}
                            />
                        </Modal>

                        {/* Blocked: default on another account */}
                        <Modal
                            isOpen={showOtherAccountDefaultBlockModal}
                            onClose={() => setShowOtherAccountDefaultBlockModal(false)}
                            ariaLabelledby="modal-title"
                        >
                            <ConfirmModalContent
                                title="Payment Method Set As Default"
                                message={<>This payment method is a default payment method on another account you manage. To delete this payment method, first assign another payment method as the <Badge color="blue">Default</Badge> payment method on the other account.</>}
                                actionText="Okay"
                                showCancel={false}
                                onAction={() => setShowOtherAccountDefaultBlockModal(false)}
                                onCancel={() => setShowOtherAccountDefaultBlockModal(false)}
                            />
                        </Modal>

                        {/* Confirmation: proceed with deletion */}
                        <Modal
                            isOpen={showDeleteModal}
                            onClose={() => setShowDeleteModal(false)}
                            ariaLabelledby="modal-title"
                        >
                            <ConfirmModalContent
                                title="Delete Payment Method"
                                message="This will fully remove the payment method from your account. If you want to use it in the future, it will need to be added again."
                                actionText="Okay, Delete"
                                onAction={() => {
                                    onDelete(idPaymentMethod);
                                    setShowDeleteModal(false);
                                }}
                                showCancel={true}
                                onCancel={() => setShowDeleteModal(false)}
                            />
                        </Modal>
                    </>
                )}
            </div>
        </div>
    );
};
